import { Props } from 'react-select';
import { StoreGroup } from '@/common/types/purchasing';
import { useDefaultOptionsQuery } from '@/library/components/form/inputs/select/api/queries';
import { DropdownIndicator } from '@/common/components/form/inputs/Select';
import { getAscendanceTree, normalizeTreeStructure } from '@/library/utils/tree-structure';
import {
  buildAndSortStoreGroupsTree,
  OptionWithParentIcon
} from '@/common/components/selectors/_storesSelectComponents';

import Select, { SelectProps } from '@/library/components/form/inputs/select';
import IconSelect, { IconSelectProps } from '@/library/components/form/inputs/select/icon-select';
import SelectLoaderIndicator from '@/common/components/form/helpers/SelectLoaderIndicator';
import _uniqBy from 'lodash/uniqBy';
import _sortBy from 'lodash/sortBy';

type BaseCollectionFilterProps<Option> = {
  value: string[] | null;
  onChange: (value: string[] | null) => void;
} & SelectProps<Option>;

type CollectionFilterProps<Option> =
  | (BaseCollectionFilterProps<Option> & { icon?: undefined }) // when there's no icon
  | (BaseCollectionFilterProps<Option> & IconSelectProps); // when the icon is provided

const defaultMemoizedRequestParams = {
  path: 'lists',
  params: { list: 'store-groups' }
};

function StoreGroupsCollectionFilterSelector<Option extends StoreGroup>({
  value,
  icon,
  onChange,
  isMulti = true,
  ...rest
}: Omit<Props<Option>, 'value'> & CollectionFilterProps<Option>) {
  const initialOptionsQuery = useDefaultOptionsQuery(defaultMemoizedRequestParams);

  const options = (initialOptionsQuery.data || []) as Option[];
  const sortedTree = buildAndSortStoreGroupsTree(options);
  const normalizedTree = normalizeTreeStructure(sortedTree);

  const selectedOptions = options.filter(o => value && value.includes(`${o.id}`));

  const onLoadOptions = (inputValue: string, callback: (options: Option[]) => void) => {
    const search = inputValue.toLowerCase();

    const groupsInSearch = options
      .filter(
        o =>
          (o.code && o.code.toLowerCase().includes(search)) || o.name.toLowerCase().includes(search)
      )
      .filter((_, index) => index < 10);

    let parsedGroupsInSearch: Option[] = [];

    groupsInSearch.forEach(optInSearch => {
      if (optInSearch.parent_id) {
        parsedGroupsInSearch = [
          ...(getAscendanceTree(optInSearch.id, normalizedTree) as Option[]),
          ...parsedGroupsInSearch
        ];
      }

      parsedGroupsInSearch.push(optInSearch);
    });

    callback(_sortBy(_uniqBy(parsedGroupsInSearch, 'id'), 'sort_index'));
  };

  const CollectionFilterComponent = icon ? IconSelect : Select;

  return (
    <CollectionFilterComponent
      placeholder="Select value"
      icon={icon ? icon : ''}
      autoFocus={true}
      {...rest}
      getOptionValue={option => `${option.id}`}
      getOptionLabel={option => `${option.code ? `${option.code}. ` : ''}${option.name}`}
      memoizedRequestParams={{ ...defaultMemoizedRequestParams, enabled: false }}
      components={{
        Option: OptionWithParentIcon<Option>,
        DropdownIndicator: initialOptionsQuery.isLoading ? SelectLoaderIndicator : DropdownIndicator
      }}
      value={
        !isMulti
          ? selectedOptions.length
            ? (selectedOptions[0] as unknown as Option)
            : null
          : (selectedOptions as unknown as Option[])
      } // Type assertion to make TypeScript happy
      onChange={selected => {
        if (isMulti) {
          const selectedValues = selected as Option[] | null;

          onChange(selectedValues?.length ? selectedValues.map(({ id }) => id.toString()) : null);
        } else {
          const selectedValue = selected as Option | null;

          onChange(selectedValue ? [selectedValue.id.toString()] : null);
        }
      }}
      isAsync={true}
      isMulti={isMulti}
      defaultOptions={((initialOptionsQuery.data || []) as Option[]).filter(
        (o: Option) => !o.parent_id
      )} // In the default options we show only root lvl groups
      loadOptions={onLoadOptions}
    />
  );
}

export default StoreGroupsCollectionFilterSelector;
